/**
 * Motor Vehicle BIK Tab - SIMPLIFIED
 * 
 * Read-only dashboard for vehicle BIK status.
 * Maps to IRAS Appendix 8A section d5 (Motor Vehicle).
 * 
 * Policy: All private vehicles (S plates) use uniform 3/7 rule.
 * BIK is calculated automatically from Asset Portal data.
 * 
 * NO manual assignment configuration required.
 * 
 * Design System: Admin Theme (Red)
 */

import React, { useState } from 'react';
import {
  Car,
  DollarSign,
  RefreshCw,
  User,
  Calendar,
  Info,
  Shield,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronRight,
} from 'lucide-react';
import { useBIKReconciliation } from '../../../../hooks/useBIKData';
import LoadingSpinner from '../../../../components/UI/LoadingSpinner';
import BIKPolicyInfo from './BIKPolicyInfo';
import { getBIKFormulaText } from '../../../../constants/bikConstants';
import IconButton from '../../../../components/UI/IconButton';
import { formatDate } from '../../../../utils/dateUtils';
import AssetDetailModal from '../../../../components/AssetDetailModal';
import VehicleDerivation from './VehicleDerivation';

const MotorVehicleBIKTab = ({ year }) => {
  const [showCommercialList, setShowCommercialList] = useState(false);
  // Read-only Asset Portal detail modal, opened by Asset PK (secondary action).
  const [detailAssetId, setDetailAssetId] = useState(null);
  // Which private-vehicle rows have their IRAS derivation expanded.
  const [expandedKeys, setExpandedKeys] = useState(() => new Set());
  const openVehicleDetail = (assetId) => {
    if (assetId != null) setDetailAssetId(assetId);
  };
  const toggleExpanded = (key) => {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  // Fetch reconciliation data from backend (year-aware)
  const { 
    data: reconciliation, 
    isLoading, 
    refetch 
  } = useBIKReconciliation(year);
  
  const formatCurrency = (value) => {
    const num = parseFloat(value || 0);
    return num.toLocaleString('en-SG', { 
      style: 'currency', 
      currency: 'SGD',
      minimumFractionDigits: 2 
    });
  };
  
  // Loading skeleton
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    );
  }
  
  // Extract data from reconciliation response
  const privateWithBIK = reconciliation?.private_with_bik || [];
  const commercialExempt = reconciliation?.commercial_exempt || [];
  const unknownPlates = reconciliation?.unknown_plates || [];
  const privateNoAssignee = reconciliation?.private_no_assignee || [];
  
  // Total from backend, or sum of per-vehicle estimated_annual_bik as fallback
  const totalEstimatedBIK = reconciliation?.summary?.total_estimated_annual_bik != null
    ? parseFloat(reconciliation.summary.total_estimated_annual_bik)
    : (privateWithBIK.reduce((sum, v) => sum + parseFloat(v.estimated_annual_bik || 0), 0));
  
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
              Motor Vehicle BIK Dashboard
            </h2>
            <p className="text-xs sm:text-sm text-gray-600 truncate">
              Automatic BIK calculation for private vehicles
            </p>
          </div>
          
          <div className="flex-shrink-0">
            <IconButton
              icon={RefreshCw}
              onClick={() => refetch()}
              title="Refresh"
              ariaLabel="Refresh motor vehicle BIK data"
              variant="default"
            />
          </div>
        </div>
      </div>
      
      {/* Policy Info Box */}
      <BIKPolicyInfo variant="blue" expandable={false} showFormula={true} />
      
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-6">
        {/* Commercial Exempt */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{commercialExempt.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Commercial (BIK Exempt)</p>
            </div>
          </div>
        </div>
        
        {/* Private with BIK */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900">{privateWithBIK.length}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Private (BIK Auto-Calculated)</p>
            </div>
          </div>
        </div>
        
        {/* Total Estimated BIK */}
        <div className="bg-white rounded-2xl shadow-sm border p-4 sm:p-6">
          <div className="flex items-center">
            <div className="w-9 h-9 sm:w-12 sm:h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <DollarSign className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
            </div>
            <div className="ml-2 sm:ml-4 min-w-0">
              <p className="text-lg sm:text-2xl font-bold text-gray-900 truncate">{formatCurrency(totalEstimatedBIK)}</p>
              <p className="text-xs sm:text-sm text-gray-600 truncate">Est. Annual BIK (d5)</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Unknown Plates Warning */}
      {unknownPlates.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-amber-200 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-amber-800">
                {unknownPlates.length} Vehicle(s) with Unknown Plate Format
              </h3>
              <p className="mt-1 text-sm text-amber-700 break-words">
                These vehicles have plate numbers that don't match known Singapore formats (G/GB/S).
                Please review in Asset Portal: {unknownPlates.map(v => v.vehicle_number).join(', ')}
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Private Vehicles - BIK Auto-Calculated */}
      <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
        <div className="px-6 py-4 border-b bg-red-50">
          <h3 className="text-sm font-semibold text-red-800 flex items-center">
            <Car className="w-4 h-4 mr-2" />
            Private Vehicles - BIK Auto-Calculated ({privateWithBIK.length})
          </h3>
          <p className="text-xs text-red-600 mt-1" data-testid="motor-bik-year">
            Data for tax year <strong>{reconciliation?.summary?.year ?? year}</strong> (YOA {(reconciliation?.summary?.year ?? year) + 1})
          </p>
        </div>
        {privateWithBIK.length === 0 ? (
          /* Shared empty state */
          <div className="px-6 py-12 text-center text-gray-500">
            <Car className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p>No private vehicles currently assigned to employees</p>
          </div>
        ) : (
          <>
            {/* Mobile: stacked cards */}
            <div className="md:hidden p-4 space-y-3">
              {privateWithBIK.map((vehicle, idx) => {
                const rowKey = `${vehicle.id}-${vehicle.current_assignee_staff_id || ''}-${vehicle.assigned_date || ''}-${idx}`;
                const isExpanded = expandedKeys.has(rowKey);
                return (
                <div
                  key={rowKey}
                  className="bg-white border border-gray-200 rounded-xl p-4 cursor-pointer active:bg-gray-50"
                  onClick={() => toggleExpanded(rowKey)}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{vehicle.vehicle_number}</p>
                      <p className="text-xs text-gray-500 truncate">{vehicle.brand} {vehicle.model}</p>
                    </div>
                    <span className="text-gray-400 flex-shrink-0">
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </span>
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Assignee</span>
                      <span className="text-gray-900 text-right min-w-0">
                        <span className="block truncate">{vehicle.current_assignee || '-'}</span>
                        {vehicle.current_assignee_staff_id && (
                          <span className="block text-xs text-gray-500 truncate">{vehicle.current_assignee_staff_id}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Assigned Since</span>
                      <span className="text-gray-900 text-right min-w-0">
                        {formatDate(vehicle.assigned_date)}
                        {vehicle.days_assigned != null && (
                          <span className="block text-xs text-gray-500 truncate">{vehicle.days_assigned} days in {year}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Ownership</span>
                      <span className="text-gray-900 text-right truncate min-w-0">
                        {vehicle.ownership_type_display || '—'}
                        {vehicle.lessor_company && (
                          <span className="block text-xs text-gray-500 truncate">{vehicle.lessor_company}</span>
                        )}
                      </span>
                    </div>
                    <div className="flex items-start justify-between gap-3 text-sm">
                      <span className="text-gray-500 flex-shrink-0">Est. Annual BIK</span>
                      <span className="text-red-600 text-right truncate min-w-0 tabular-nums font-semibold">
                        {formatCurrency(vehicle.estimated_annual_bik)}
                        {vehicle.calc_method === 'fallback_approx' && (
                          <span className="block text-xs text-amber-600 font-normal">estimated</span>
                        )}
                      </span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-gray-100" onClick={(e) => e.stopPropagation()}>
                      <VehicleDerivation
                        derivation={vehicle.derivation}
                        annual={vehicle.annual_car_benefit}
                        prorated={vehicle.estimated_annual_bik}
                        daysAssigned={vehicle.days_assigned}
                        year={reconciliation?.summary?.year ?? year}
                      />
                      {vehicle.asset_id != null && (
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={() => openVehicleDetail(vehicle.asset_id)}
                            className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                          >
                            View full vehicle record
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
                );
              })}
              {/* Mobile total (mirrors the desktop tfoot) */}
              <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-center justify-between gap-3 text-sm">
                <span className="font-medium text-gray-700">Total Estimated Annual BIK</span>
                <span className="font-bold text-red-600 tabular-nums">{formatCurrency(totalEstimatedBIK)}</span>
              </div>
            </div>
            
            {/* Desktop: full table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Assignee</th>
                    <th className="table-header-cell">Assigned Since</th>
                    <th className="table-header-cell" title="Owned | Leased | Rented">Ownership</th>
                    <th className="table-header-cell text-right" title="IRAS capital component per year: (car cost − PARF rebate) / 10 for an owned car; nil for leased">Capital/yr</th>
                    <th className="table-header-cell text-right" title="Actual running & maintenance incl. petrol, road tax, insurance (lease rental for leased)">Running &amp; Maint.</th>
                    <th className="table-header-cell text-right" title="(Capital + Running) × 3/7, prorated by days assigned">Est. Annual BIK</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {privateWithBIK.map((vehicle, idx) => {
                    const rowKey = `${vehicle.id}-${vehicle.current_assignee_staff_id || ''}-${vehicle.assigned_date || ''}-${idx}`;
                    const isExpanded = expandedKeys.has(rowKey);
                    return (
                    <React.Fragment key={rowKey}>
                    <tr
                      className="table-row-dense cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleExpanded(rowKey)}
                      title="Show IRAS calculation"
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-400">
                            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </span>
                          <Car className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">{vehicle.vehicle_number}</span>
                            <p className="text-xs text-gray-500">{vehicle.brand} {vehicle.model}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <span className="text-sm font-medium text-gray-900">{vehicle.current_assignee || '-'}</span>
                            {vehicle.current_assignee_staff_id && (
                              <p className="text-xs text-gray-500">{vehicle.current_assignee_staff_id}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          <div>
                            <span className="text-sm text-gray-900">{formatDate(vehicle.assigned_date)}</span>
                            {vehicle.days_assigned != null && (
                              <p className="text-xs text-gray-500">{vehicle.days_assigned} days in {year}</p>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className="text-sm text-gray-900">{vehicle.ownership_type_display || '—'}</span>
                        {vehicle.lessor_company && (
                          <p className="text-xs text-gray-500 truncate">{vehicle.lessor_company}</p>
                        )}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-gray-900 tabular-nums">
                          {vehicle.capital_component != null ? formatCurrency(vehicle.capital_component) : '—'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm text-gray-900 tabular-nums">
                          {formatCurrency(vehicle.running_maintenance ?? vehicle.annual_running_costs)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <span className="text-sm font-medium text-red-600 tabular-nums">
                          {formatCurrency(vehicle.estimated_annual_bik)}
                        </span>
                        {vehicle.calc_method === 'fallback_approx' && (
                          <p className="text-xs text-amber-600" title="Car cost/PARF not recorded - using legacy approximation. Complete the log-card data in Asset Portal for the exact IRAS figure.">
                            estimated
                          </p>
                        )}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-gray-50">
                        <td colSpan="7" className="px-6 py-4">
                          <div className="max-w-xl">
                            <p className="text-xs font-semibold text-gray-700 mb-2">
                              How this BIK was calculated (IRAS Appendix 8A, d5)
                            </p>
                            <VehicleDerivation
                              derivation={vehicle.derivation}
                              annual={vehicle.annual_car_benefit}
                              prorated={vehicle.estimated_annual_bik}
                              daysAssigned={vehicle.days_assigned}
                              year={reconciliation?.summary?.year ?? year}
                            />
                            {vehicle.asset_id != null && (
                              <div className="mt-2">
                                <button
                                  onClick={() => openVehicleDetail(vehicle.asset_id)}
                                  className="text-xs font-medium text-red-600 hover:text-red-800 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                                >
                                  View full vehicle record
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="bg-gray-50">
                  <tr>
                    <td colSpan="6" className="px-6 py-3 text-right text-sm font-medium text-gray-700">
                      Total Estimated Annual BIK:
                    </td>
                    <td className="px-6 py-3 text-right text-sm font-bold text-red-600">
                      {formatCurrency(totalEstimatedBIK)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </>
        )}
      </div>
      
      {/* Commercial vehicles – one-line summary and optional [View list] */}
      {commercialExempt.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border p-4">
          <p className="text-sm text-gray-600 flex items-center gap-2 flex-wrap">
            <Shield className="w-4 h-4 text-green-600 flex-shrink-0" />
            <span>{commercialExempt.length} commercial vehicle{commercialExempt.length !== 1 ? 's' : ''} (G/GB) – BIK-exempt.</span>
            <button
              type="button"
              onClick={() => setShowCommercialList((v) => !v)}
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium text-green-700 hover:bg-green-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-green-500"
            >
              {showCommercialList ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              {showCommercialList ? 'Hide list' : 'View list'}
            </button>
          </p>
          {showCommercialList && (
            <div className="mt-3 overflow-x-auto border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="table-header">
                  <tr>
                    <th className="table-header-cell">Vehicle</th>
                    <th className="table-header-cell">Assignee</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {commercialExempt.map((v) => (
                    <tr
                      key={v.id}
                      className={`table-row-dense ${v.asset_id != null ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                      onClick={v.asset_id != null ? () => openVehicleDetail(v.asset_id) : undefined}
                      title={v.asset_id != null ? 'View vehicle details (read-only)' : undefined}
                    >
                      <td className="px-4 py-2">
                        <span className="text-sm font-medium text-gray-900 hover:text-blue-600">{v.vehicle_number}</span>
                        <p className="text-xs text-gray-500">{v.brand} {v.model}</p>
                      </td>
                      <td className="px-4 py-2">
                        <span className="text-sm text-gray-900">{v.current_assignee || '—'}</span>
                        {v.current_assignee_staff_id && <p className="text-xs text-gray-500">{v.current_assignee_staff_id}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      
      {/* Private vehicles with NO current assignee - taxable plate but idle */}
      {privateNoAssignee.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border overflow-hidden">
          <div className="px-6 py-4 border-b bg-amber-50">
            <h3 className="text-sm font-semibold text-amber-800 flex items-center">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Private Vehicles - No Current Assignee ({privateNoAssignee.length})
            </h3>
            <p className="text-xs text-amber-700 mt-1">
              Taxable (S/Q) plates not assigned to any employee in {year}. No BIK is charged while unassigned - review whether they should be assigned or disposed.
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Vehicle</th>
                  <th className="table-header-cell">Ownership</th>
                  <th className="table-header-cell text-right">Running &amp; Maint.</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {privateNoAssignee.map((v) => (
                  <tr
                    key={v.id}
                    className={`table-row-dense ${v.asset_id != null ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                    onClick={v.asset_id != null ? () => openVehicleDetail(v.asset_id) : undefined}
                    title={v.asset_id != null ? 'View vehicle details (read-only)' : undefined}
                  >
                    <td className="px-6 py-3">
                      <span className="text-sm font-medium text-gray-900 hover:text-blue-600">{v.vehicle_number}</span>
                      <p className="text-xs text-gray-500">{v.brand} {v.model}</p>
                    </td>
                    <td className="px-6 py-3">
                      <span className="text-sm text-gray-900">{v.ownership_type_display || '—'}</span>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <span className="text-sm text-gray-900 tabular-nums">
                        {formatCurrency(v.running_maintenance ?? v.annual_running_costs)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Running Costs Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-2xl p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
            <Info className="w-5 h-5 text-gray-600" />
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-700 mb-2">IRAS Appendix 8A (d5) car-benefit calculation:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="font-medium text-gray-700">Capital component (per year):</p>
                <ul className="list-disc list-inside space-y-1 mt-1">
                  <li><strong>New car:</strong> (car cost − PARF rebate) ÷ 10</li>
                  <li><strong>Second-hand:</strong> (car cost − PARF rebate) ÷ B (years to COE expiry)</li>
                  <li><strong>Renewed COE:</strong> (COE renewal + PARF) ÷ E (years to renewed COE expiry)</li>
                  <li><strong>Leased/Rented:</strong> none (rental counts as running cost)</li>
                  <li><em>"estimated" tag = car cost/PARF not recorded; legacy 20% approximation used.</em></li>
                </ul>
              </div>
              <div>
                <p className="font-medium text-gray-700">Running &amp; maintenance + formula:</p>
                <ul className="list-disc list-inside space-y-1 mt-1">
                  <li><strong>Running &amp; Maint.:</strong> petrol + road tax + insurance + maintenance (lease × 12 for leased)</li>
                  <li><strong>Calculation:</strong> {getBIKFormulaText().replace('Running Costs', '(Capital + Running)')}</li>
                  <li><strong>Prorated:</strong> Annual BIK × (Days Assigned / Days in Year)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Read-only Asset Portal detail (HR-facing - no edit/assign/transfer) */}
      <AssetDetailModal
        isOpen={detailAssetId != null}
        assetId={detailAssetId}
        onClose={() => setDetailAssetId(null)}
        readOnly
      />
    </div>
  );
};

export default MotorVehicleBIKTab;
